import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Copy, Loader2 } from 'lucide-react';

export const DuplicateCourseDialog = ({ course, onClose, onDuplicated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (course) {
      setTitle(`${course.title} (Copy)`);
      setDescription(course.description || '');
    }
  }, [course]);

  const submit = async () => {
    if (!title.trim() || !description.trim()) return toast.error('Title and description are required');
    setSaving(true);
    try {
      const res = await api.post(`/admin/courses/${course.id}/duplicate`, { title, description });
      toast.success(`Course duplicated with ${res.data.lesson_count} lessons and ${res.data.quiz_count} quizzes`);
      onDuplicated?.(res.data.course_id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate course');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!course} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="duplicate-course-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>Duplicate Course</DialogTitle>
          <DialogDescription>
            Copies all lessons, their video links, files and quizzes from "{course?.title}". Videos are linked, not re-uploaded. Enrolments, declarations and letters are not copied.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="dup-title">New course title</Label>
            <Input id="dup-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} data-testid="duplicate-title-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dup-desc">New course description</Label>
            <Textarea id="dup-desc" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="duplicate-description-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="duplicate-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} style={{ backgroundColor: '#163329' }} data-testid="duplicate-confirm-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Create Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
